/**
 * JSON Workflow Types
 *
 * Defines TypeScript interfaces for JSON-based workflow creation.
 * Supports both Full Format (editor-compatible) and Simplified Format (LLM-friendly).
 */

// =============================================================================
// Node Type Definitions
// =============================================================================

/**
 * All supported node types in the workflow system
 */
export type WorkflowNodeType =
  // Trigger nodes
  | 'manualTrigger'
  | 'scheduleTrigger'
  | 'webhookTrigger'
  | 'gmailTrigger'
  | 'googleSheetsTrigger'
  | 'httpTrigger'
  | 'slackTrigger'
  | 'accountCreated'
  | 'accountUpdated'
  | 'contactCreated'
  | 'contactUpdated'
  // Canonical trigger node names (with "Node" suffix)
  | 'accountCreatedNode'
  | 'accountUpdatedNode'
  | 'contactCreatedNode'
  | 'contactUpdatedNode'
  | 'cmpEngagementNode'
  // Logic nodes
  | 'if'
  | 'switch'
  | 'condition'
  | 'conditionNode'
  | 'merge'
  | 'mergeNode'
  | 'set'
  | 'mapFields'
  | 'code'
  | 'codeNode'
  | 'defineVariablesNode'
  | 'wait'
  // Communication nodes
  | 'httpRequest'
  | 'httpNode'
  | 'gmail'
  | 'gmailNode'
  | 'slack'
  | 'slackNode'
  | 'teams'
  | 'webhook'
  | 'webhookNode'
  // Google nodes
  | 'googleSheets'
  | 'googleSheetNode'
  // AI nodes
  | 'openai'
  | 'anthropic'
  | 'llm'
  | 'llmNode'
  | 'generateMessage'
  | 'hpm'
  | 'hpmNode'
  // Platform-specific nodes
  | 'addToCampaign'
  | 'addToCampaignNode'
  | 'enrichContact'
  | 'enrichContactNode'
  | 'sourceContact'
  | 'sourceContactNode'
  | 'moveToTAL'
  | 'moveToTALNode'
  | 'addContactToTal'
  | 'moveToAccountTAL'
  | 'moveToAccountTALNode'
  | 'addAccountToTal'
  | 'assignContact'
  | 'assignContactNode'
  | 'criteriaContact'
  | 'criteriaContactNode'
  | 'criteriaAccount'
  | 'criteriaAccountNode'
  | 'linkedinSearch'
  | 'linkedinSearchNode'
  | 'enrichDomain'
  | 'enrichDomainNode'
  | 'personLinkedinLookup'
  | 'addToListNode'
  | 'addAccountsByICP'
  | 'databaseSearchNode';

/**
 * Categories of node types for organization
 */
export type NodeCategory =
  | 'trigger'
  | 'logic'
  | 'communication'
  | 'ai'
  | 'platform'
  | 'data';

/**
 * Mapping of node types to their categories
 */
export const NODE_TYPE_CATEGORIES: Record<WorkflowNodeType, NodeCategory> = {
  // Triggers
  manualTrigger: 'trigger',
  scheduleTrigger: 'trigger',
  webhookTrigger: 'trigger',
  gmailTrigger: 'trigger',
  googleSheetsTrigger: 'trigger',
  httpTrigger: 'trigger',
  slackTrigger: 'trigger',
  accountCreated: 'trigger',
  accountUpdated: 'trigger',
  contactCreated: 'trigger',
  contactUpdated: 'trigger',
  accountCreatedNode: 'trigger',
  accountUpdatedNode: 'trigger',
  contactCreatedNode: 'trigger',
  contactUpdatedNode: 'trigger',
  cmpEngagementNode: 'trigger',
  // Logic
  if: 'logic',
  switch: 'logic',
  condition: 'logic',
  conditionNode: 'logic',
  merge: 'logic',
  mergeNode: 'logic',
  set: 'logic',
  mapFields: 'data',
  code: 'logic',
  codeNode: 'logic',
  defineVariablesNode: 'logic',
  wait: 'logic',
  // Communication
  httpRequest: 'communication',
  httpNode: 'communication',
  gmail: 'communication',
  gmailNode: 'communication',
  slack: 'communication',
  slackNode: 'communication',
  teams: 'communication',
  webhook: 'communication',
  webhookNode: 'communication',
  googleSheets: 'data',
  googleSheetNode: 'data',
  // AI
  openai: 'ai',
  anthropic: 'ai',
  llm: 'ai',
  llmNode: 'ai',
  generateMessage: 'ai',
  hpm: 'ai',
  hpmNode: 'ai',
  // Platform
  addToCampaign: 'platform',
  addToCampaignNode: 'platform',
  enrichContact: 'platform',
  enrichContactNode: 'platform',
  sourceContact: 'platform',
  sourceContactNode: 'platform',
  moveToTAL: 'platform',
  moveToTALNode: 'platform',
  addContactToTal: 'platform',
  moveToAccountTAL: 'platform',
  moveToAccountTALNode: 'platform',
  addAccountToTal: 'platform',
  assignContact: 'platform',
  assignContactNode: 'platform',
  criteriaContact: 'platform',
  criteriaContactNode: 'platform',
  criteriaAccount: 'platform',
  criteriaAccountNode: 'platform',
  linkedinSearch: 'platform',
  linkedinSearchNode: 'platform',
  enrichDomain: 'platform',
  enrichDomainNode: 'platform',
  personLinkedinLookup: 'platform',
  addToListNode: 'platform',
  addAccountsByICP: 'platform',
  databaseSearchNode: 'platform',
};

/**
 * Set of trigger node types (for validation)
 */
export const TRIGGER_NODE_TYPES = new Set<WorkflowNodeType>([
  // Canonical trigger types (from PlaybookWorkflow/constants.ts)
  'scheduleTrigger',
  'googleSheetsTrigger',
  'accountCreatedNode',
  'accountUpdatedNode',
  'contactCreatedNode',
  'contactUpdatedNode',
  'cmpEngagementNode',
  // Other triggers
  'webhookTrigger',
  'gmailTrigger',
  'httpTrigger',
  'slackTrigger',
  // Legacy names (without Node suffix) for backwards compatibility
  'manualTrigger',
  'accountCreated',
  'accountUpdated',
  'contactCreated',
  'contactUpdated',
]);

/**
 * Set of conditional node types (for validation)
 */
export const CONDITIONAL_NODE_TYPES = new Set<WorkflowNodeType>([
  'if',
  'switch',
  'condition',
  'conditionNode',
]);

// =============================================================================
// Full Format Types (Editor-Compatible)
// =============================================================================

/**
 * Position of a node on the canvas
 */
export interface NodePosition {
  x: number;
  y: number;
}

/**
 * Node data structure for the full format
 */
export interface WorkflowNodeData {
  nodeType: WorkflowNodeType | string;
  label?: string;
  name?: string;
  configured: boolean;
  parameters?: Record<string, unknown>;
  [key: string]: unknown;
}

/**
 * Full node definition (editor-compatible)
 */
export interface WorkflowNodeJSON {
  id: string;
  type: WorkflowNodeType | string;
  position: NodePosition;
  data: WorkflowNodeData;
}

/**
 * Edge definition connecting two nodes
 */
export interface WorkflowEdgeJSON {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string; // For conditional nodes: 'true', 'false', 'case-0', etc.
  targetHandle?: string;
}

/**
 * Full workflow JSON format (editor-compatible)
 */
export interface WorkflowJSON {
  version?: string;
  playbookName: string;
  description?: string;
  entityType?: 'CONTACT' | 'ACCOUNT' | string;
  templateType?: string;
  triggerType?: string;
  status?: 'DRAFT' | 'ACTIVE' | string;
  nodes: WorkflowNodeJSON[];
  edges: WorkflowEdgeJSON[];
  metadata?: Record<string, unknown>;
}

// =============================================================================
// Simplified Format Types (LLM-Friendly)
// =============================================================================

/**
 * Simplified step definition (for LLM-generated workflows)
 */
export interface SimplifiedStepJSON {
  name?: string;
  type: WorkflowNodeType | string;
  config?: Record<string, unknown>;
  // For conditional branches
  onTrue?: SimplifiedStepJSON[];
  onFalse?: SimplifiedStepJSON[];
  // For switch node branches
  cases?: Array<{
    value: string;
    operator?: string;
    steps: SimplifiedStepJSON[];
  }>;
  defaultCase?: SimplifiedStepJSON[];
}

/**
 * Simplified workflow JSON format (LLM-friendly)
 */
export interface SimplifiedWorkflowJSON {
  name: string;
  description?: string;
  entityType?: 'CONTACT' | 'ACCOUNT' | string;
  steps: SimplifiedStepJSON[];
  metadata?: Record<string, unknown>;
}

// =============================================================================
// Union Types and Detection
// =============================================================================

/**
 * Union of both workflow JSON formats
 */
export type AnyWorkflowJSON = WorkflowJSON | SimplifiedWorkflowJSON;

/**
 * Detected format type
 */
export type WorkflowJSONFormat = 'full' | 'simplified';

/**
 * Type guard for full format
 */
export function isFullFormat(json: AnyWorkflowJSON): json is WorkflowJSON {
  return 'nodes' in json && Array.isArray((json as WorkflowJSON).nodes);
}

/**
 * Type guard for simplified format
 */
export function isSimplifiedFormat(json: AnyWorkflowJSON): json is SimplifiedWorkflowJSON {
  return 'steps' in json && Array.isArray((json as SimplifiedWorkflowJSON).steps);
}

/**
 * Detect the format of a workflow JSON
 */
export function detectWorkflowFormat(json: unknown): WorkflowJSONFormat {
  if (!json || typeof json !== 'object') {
    throw new Error('INVALID_JSON: Input must be a valid JSON object');
  }

  const obj = json as Record<string, unknown>;

  if ('steps' in obj && Array.isArray(obj.steps)) {
    return 'simplified';
  }

  if ('nodes' in obj && Array.isArray(obj.nodes)) {
    return 'full';
  }

  throw new Error('INVALID_FORMAT: JSON must have either "nodes" (full format) or "steps" (simplified format)');
}

// =============================================================================
// Service Types
// =============================================================================

/**
 * Options for JSON workflow operations
 */
export interface WorkflowJSONOptions {
  /** Auto-layout nodes if positions not provided (default: true) */
  autoLayout?: boolean;
  /** Auto-generate IDs for nodes/edges (default: true) */
  autoGenerateIds?: boolean;
  /** Only validate without creating (default: false) */
  validateOnly?: boolean;
  /** Return full format in response (default: true) */
  returnFullFormat?: boolean;
}

/**
 * Result of workflow creation from JSON
 */
export interface CreateWorkflowFromJSONResult {
  success: boolean;
  data?: {
    id: string | number;
    workflow: WorkflowJSON;
  };
  validation: WorkflowJSONValidationResult;
}

/**
 * Validation error for JSON workflow
 */
export interface WorkflowJSONValidationError {
  code: WorkflowJSONErrorCode;
  message: string;
  path?: string;
  nodeId?: string;
  edgeId?: string;
  suggestion?: string;
}

/**
 * Validation warning for JSON workflow
 */
export interface WorkflowJSONValidationWarning {
  code: string;
  message: string;
  path?: string;
  nodeId?: string;
  suggestion?: string;
}

/**
 * Validation result for JSON workflow
 */
export interface WorkflowJSONValidationResult {
  isValid: boolean;
  errors: WorkflowJSONValidationError[];
  warnings: WorkflowJSONValidationWarning[];
}

/**
 * Error codes for JSON workflow validation
 */
export type WorkflowJSONErrorCode =
  | 'INVALID_JSON'
  | 'INVALID_FORMAT'
  | 'MISSING_REQUIRED_FIELD'
  | 'INVALID_NODE_TYPE'
  | 'MISSING_TRIGGER'
  | 'DISCONNECTED_NODE'
  | 'INVALID_EDGE'
  | 'CYCLE_DETECTED'
  | 'MISSING_NODE_PARAMETER'
  | 'INVALID_NODE_PARAMETER'
  | 'DUPLICATE_NODE_ID'
  | 'DUPLICATE_EDGE_ID'
  | 'MISSING_CONDITIONAL_BRANCH';

// =============================================================================
// Constants
// =============================================================================

/**
 * Auto-layout constants for node positioning
 */
export const AUTO_LAYOUT_CONSTANTS = {
  START_X: 560,
  START_Y: 20,
  NODE_HEIGHT: 120,
  GAP_Y: 40,
  BRANCH_OFFSET_X: 300,
  MERGE_OFFSET_Y: 80,
} as const;

/**
 * Current JSON workflow schema version
 */
export const WORKFLOW_JSON_VERSION = '1.0';
